import { useEffect, useMemo, type PropsWithChildren } from "react"
import createCache, { type EmotionCache } from "@emotion/cache"
import { CacheProvider } from "@emotion/react"
import CssBaseline from "@mui/material/CssBaseline"
import createTheme from "@mui/material/styles/createTheme"
import ThemeProvider from "@mui/material/styles/ThemeProvider"
import { prefixer } from "stylis"
import rtlPlugin from "stylis-plugin-rtl"

import { useAppSelector } from "@/lib/hooks"
import useDirection from "../hooks/use_direction"

const longChatsLanguageVariables = {
  isLongCharWidth: true,
  fontSizes: {
    text: "15px",
    actionTitle: "17px",
    title: "22px",
  },
}

const defaultLanguageVariables = {
  isLongCharWidth: false,
  fontSizes: {
    text: "16px",
    actionTitle: "20px",
    title: "24px",
  },
}

const theme = createTheme({
  typography: {
    button: {
      textTransform: "none",
    },
  },
  variables: {
    isLtr: false,
    ...defaultLanguageVariables,
  },
  spacing: 1,
  palette: {
    default: {
      main: "rgb(0, 0, 0)",
      dark: "rgb(0, 0, 0)",
    },
  },
  breakpoints: {
    values: {
      mobile: 0,
      desktop: 731,
    } as never,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "rgba(0, 0, 0, 0.05)",
        },
        "body #vg-tooltip-element": {
          fontFamily: "inherit",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: "inherit",
        },
      },
    },
  },
})

const createRtlCache = (): EmotionCache =>
  createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
  })

const createLtrCache = (): EmotionCache =>
  createCache({
    key: "muiltr",
  })

const Theme = ({ children }: PropsWithChildren) => {
  const currentLanguageCode = useAppSelector((state) => state.currentLanguageCode)
  const languageDirection = useDirection()
  const isLtr = languageDirection === "ltr"

  const longCharWidthLanguages = ["ja", "en"]

  const cache = useMemo<EmotionCache>(() => (isLtr ? createLtrCache() : createRtlCache()), [isLtr])

  if (longCharWidthLanguages.includes(currentLanguageCode ?? "")) {
    Object.assign(theme.variables, longChatsLanguageVariables)
  } else {
    Object.assign(theme.variables, defaultLanguageVariables)
  }

  useEffect(() => {
    theme.variables.isLtr = isLtr
    document.dir = isLtr ? "ltr" : "rtl"
  }, [isLtr])

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </CacheProvider>
  )
}

export default Theme
